import classNames from 'classnames';
import type { ReactNode } from 'react';

import { Button } from '../Button';
import type { ButtonHierarchy } from '../Button';
import { Checkbox } from '../Checkbox';
import { Icon } from '../Icon';

import styles from './Modal.module.css';

export interface ModalAction {
  label: string;
  /** Identifier resolved to the caller of the imperative `openModal` (defaults to the label). */
  value?: string;
  /** Button hierarchy. `destructive` takes precedence and paints the button red. */
  hierarchy?: ButtonHierarchy;
  destructive?: boolean;
  disabled?: boolean;
  /** Busy state: shows the button spinner, sets `aria-busy` and disables the button. */
  loading?: boolean;
  /** Focus this button when the modal opens. */
  autoFocus?: boolean;
  iconLeading?: ReactNode;
  onClick?: () => void;
  /** Close the modal after `onClick` (standalone use only; default true). */
  closeOnClick?: boolean;
}

export interface ModalSettings {
  label?: string;
  icon?: ReactNode;
  onClick?: () => void;
}

export type ModalFooterLayout = 'horizontal' | 'stacked';

export interface ModalFooterProps {
  actions: ModalAction[];
  layout: ModalFooterLayout;
  onAction: (action: ModalAction) => void;
  dontShowAgain?: boolean;
  dontShowAgainChecked?: boolean;
  onDontShowAgainChange?: (checked: boolean) => void;
  settings?: ModalSettings;
  labels: { dontShowAgain: string; settings: string };
}

/** Footer button hierarchy: explicit `hierarchy`/`destructive` wins; else primary for the CTA. */
function resolveHierarchy(action: ModalAction, isPrimary: boolean): ButtonHierarchy {
  if (action.destructive === true) return 'destructive';
  if (action.hierarchy != null) return action.hierarchy;
  return isPrimary ? 'primary' : 'secondary';
}

/**
 * Modal action bar. `horizontal` fills the row (two equal buttons) or, with a left cluster,
 * right-aligns natural-width buttons; `stacked` lists full-width buttons with the CTA on top.
 * The optional cluster carries a `Don't show again` checkbox and a `Settings` button.
 */
export function ModalFooter({
  actions,
  layout,
  onAction,
  dontShowAgain = false,
  dontShowAgainChecked,
  onDontShowAgainChange,
  settings,
  labels,
}: ModalFooterProps) {
  const hasCluster = dontShowAgain || settings != null;
  // The CTA is the last action (rightmost when horizontal); stacked lifts it to the top.
  const ordered = layout === 'stacked' ? [...actions].reverse() : actions;
  const ctaIndex = layout === 'stacked' ? 0 : actions.length - 1;

  const actionsEl =
    actions.length > 0 ? (
      <div className={styles.actions}>
        {ordered.map((action, index) => (
          <Button
            key={action.value ?? action.label}
            hierarchy={resolveHierarchy(action, index === ctaIndex)}
            size="md"
            disabled={action.disabled}
            loading={action.loading === true}
            iconLeading={action.iconLeading}
            {...(action.autoFocus === true ? { 'data-autofocus': true } : {})}
            className={styles.action}
            onClick={() => {
              onAction(action);
            }}
          >
            {action.label}
          </Button>
        ))}
      </div>
    ) : null;

  const clusterEl = hasCluster ? (
    <div className={styles.cluster}>
      {dontShowAgain ? (
        <Checkbox
          size="sm"
          label={labels.dontShowAgain}
          {...(dontShowAgainChecked !== undefined ? { checked: dontShowAgainChecked } : {})}
          {...(onDontShowAgainChange != null ? { onChange: onDontShowAgainChange } : {})}
        />
      ) : null}
      {settings != null ? (
        <Button
          hierarchy="link-gray"
          size="sm"
          className={styles.settings}
          iconLeading={settings.icon ?? <Icon name="settings-01" />}
          aria-label={settings.label == null ? labels.settings : undefined}
          onClick={settings.onClick}
        >
          {settings.label ?? labels.settings}
        </Button>
      ) : null}
    </div>
  ) : null;

  return (
    <div className={classNames(styles.footer)} data-layout={layout} data-cluster={hasCluster}>
      {layout === 'stacked' ? (
        <>
          {actionsEl}
          {clusterEl}
        </>
      ) : (
        <>
          {clusterEl}
          {actionsEl}
        </>
      )}
    </div>
  );
}
